import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Factory that produces a community-scoped auth context.
 * Each community (nuts, caps, shark) gets its own provider + hook,
 * but they share the same underlying H0 identity (email + password) on the backend.
 *
 * @param {Object} cfg
 * @param {string} cfg.community - "nuts" | "caps" | "shark"
 * @returns {{ Provider, useAuth, TOKEN_KEY }}
 */
export function createCommunityAuthContext({ community }) {
  const TOKEN_KEY = `${community}_token`;
  const Ctx = createContext(null);

  const Provider = ({ children }) => {
    const [member, setMember] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setLoading(false);
        return;
      }
      axios
        .get(`${API_URL}/api/${community}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((r) => setMember(r.data))
        .catch(() => localStorage.removeItem(TOKEN_KEY))
        .finally(() => setLoading(false));
    }, []);

    const persistSession = (token, m) => {
      localStorage.setItem(TOKEN_KEY, token);
      setMember(m);
    };

    const refreshMember = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return null;
      try {
        const r = await axios.get(`${API_URL}/api/${community}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMember(r.data);
        return r.data;
      } catch {
        return null;
      }
    };

    const register = async (payload) => {
      const { data } = await axios.post(`${API_URL}/api/${community}/register`, payload);
      persistSession(data.access_token, data.member);
      return data;
    };

    const login = async (email, password) => {
      const { data } = await axios.post(`${API_URL}/api/${community}/login`, {
        email,
        password,
      });
      persistSession(data.access_token, data.member);
      return data;
    };

    const logout = () => {
      localStorage.removeItem(TOKEN_KEY);
      setMember(null);
    };

    const getToken = () => localStorage.getItem(TOKEN_KEY);

    // Cross-community helper: tells the UI which other communities this email is part of
    const checkEmail = async (email) => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/h0/check-email/${encodeURIComponent(email)}`
        );
        return data; // { exists, communities, full_name, nickname }
      } catch {
        return { exists: false, communities: [], full_name: null };
      }
    };

    return (
      <Ctx.Provider
        value={{ member, loading, community, register, login, logout, getToken, checkEmail, refreshMember }}
      >
        {children}
      </Ctx.Provider>
    );
  };

  const useAuth = () => {
    const ctx = useContext(Ctx);
    if (!ctx) throw new Error(`useAuth for "${community}" must be used inside its Provider`);
    return ctx;
  };

  return { Provider, useAuth, TOKEN_KEY };
}
